import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {
  static targets = ["modal", "content"]

  connect() {
    this.modal = new Modal(this.modalTarget)
  }

  showModal(event) {
    const button = event.currentTarget
    const scoreDetails = JSON.parse(button.dataset.scoreDetails)
    const participantName = button.dataset.participantName
    const judgeName = button.dataset.judgeName

    const scoreId = button.dataset.scoreId
    const isInvalidated = button.dataset.scoreInvalidated === "true"
    const invalidateUrl = button.dataset.invalidateUrl
    const restoreUrl = button.dataset.restoreUrl

    this.renderModalContent(scoreDetails, participantName, judgeName, {
      scoreId,
      isInvalidated,
      invalidateUrl,
      restoreUrl
    })
    this.modal.show()
  }

  showAllScores(event) {
    const button = event.currentTarget
    const participantName = button.dataset.participantName

    // Filter scores for this specific performance by checking if the button is in the same row
    const performanceRow = button.closest('tr')
    const scoresInRow = []

    if (performanceRow) {
      performanceRow.querySelectorAll('.score-btn').forEach(scoreBtn => {
        if (scoreBtn.dataset.scoreDetails) {
          const scoreData = JSON.parse(scoreBtn.dataset.scoreDetails)
          const judgeName = scoreBtn.dataset.judgeName
          const invalidated = scoreBtn.dataset.scoreInvalidated === "true"
          scoresInRow.push({ ...scoreData, judgeName, invalidated })
        }
      })
    }

    this.renderAllScoresContent(scoresInRow, participantName)
    this.modal.show()
  }

  renderModalContent(scoreDetails, participantName, judgeName, meta = {}) {
    const scoreId = meta.scoreId || scoreDetails.id
    const isInvalidated = meta.isInvalidated !== undefined
      ? meta.isInvalidated
      : Boolean(scoreDetails.invalidated)
    const invalidateUrl = meta.invalidateUrl
    const restoreUrl = meta.restoreUrl

    const numericScore = parseFloat(scoreDetails.total_score)
    const hasScore = Number.isFinite(numericScore)
    const totalDisplay = hasScore ? this.formatScore(numericScore) : '—'

    const statusBadge = isInvalidated
      ? '<span class="badge bg-secondary"><i class="bx bx-block me-1"></i>Đã vô hiệu</span>'
      : '<span class="badge bg-success"><i class="bx bx-check-circle me-1"></i>Đang hiệu lực</span>'

    const actionButton = (scoreId && invalidateUrl && restoreUrl)
      ? (isInvalidated
        ? `<button class="btn btn-outline-success w-100" data-action="click->score-details#restoreScore" data-url="${restoreUrl}" data-score-id="${scoreId}"><i class="bx bx-undo me-1"></i>Khôi phục điểm</button>`
        : `<button class="btn btn-outline-danger w-100" data-action="click->score-details#invalidateScore" data-url="${invalidateUrl}" data-score-id="${scoreId}"><i class="bx bx-block me-1"></i>Vô hiệu hóa điểm</button>`)
      : ''

    const statusNotice = isInvalidated
      ? '<div class="alert alert-warning border-0 mt-3 mb-0"><i class="bx bx-info-circle me-1"></i>Điểm này đang bị vô hiệu và sẽ không được tính vào kết quả xếp hạng.</div>'
      : ''

    const actionSection = actionButton
      ? `<div class="d-grid gap-2 mt-3">${actionButton}</div>`
      : ''

    const notesSection = scoreDetails.notes
      ? `
        <div class="alert alert-info border-0">
          <h6 class="alert-heading mb-2">
            <i class="bx bx-note me-1"></i>Ghi chú từ giám khảo
          </h6>
          <p class="mb-0 small">${scoreDetails.notes}</p>
        </div>
      `
      : ''

    const submittedAt = this.formatTimestamp(scoreDetails.submitted_at)

    const html = `
      <div class="text-center mb-4">
        <h6 class="text-primary fw-bold mb-1">${participantName}</h6>
        <small class="text-muted">Chấm điểm bởi ${judgeName}</small>
        <div class="mt-2">${statusBadge}</div>
      </div>

      <div class="card border-0 bg-light mb-4">
        <div class="card-body text-center py-4">
          <i class="bx bx-calculator text-primary fs-2 mb-2"></i>
          <div class="display-6 fw-bold text-primary">${totalDisplay}</div>
          <small class="text-muted d-block mt-2">Tổng điểm (60.001 - 99.999)</small>
        </div>
      </div>

      ${notesSection}
      ${statusNotice}
      ${actionSection}

      <div class="text-center pt-3 border-top mt-4">
        <small class="text-muted">
          <i class="bx bx-time me-1"></i>Thời gian chấm: ${submittedAt}
        </small>
      </div>
    `

    this.contentTarget.innerHTML = html
  }

  renderAllScoresContent(scoresArray, participantName) {
    if (scoresArray.length === 0) {
      this.contentTarget.innerHTML = `
        <div class="text-center py-4">
          <i class="bx bx-info-circle text-muted fs-1 mb-3"></i>
          <h6 class="text-muted">Chưa có điểm số</h6>
          <p class="text-muted small mb-0">Tiết mục này chưa được chấm điểm bởi giám khảo nào.</p>
        </div>
      `
      return
    }

    const validScores = scoresArray.filter(score => !score.invalidated)
    const invalidScores = scoresArray.filter(score => score.invalidated)
    const hasValidScores = validScores.length > 0

    const totalValidScore = hasValidScores
      ? validScores.reduce((sum, score) => sum + (parseFloat(score.total_score) || 0), 0)
      : 0
    const averageScore = hasValidScores ? totalValidScore / validScores.length : null

    const summaryText = hasValidScores
      ? `Có ${validScores.length}/${scoresArray.length} điểm đang hiệu lực.`
      : `Tất cả ${scoresArray.length} điểm đã bị vô hiệu - hiển thị để tham khảo.`

    const invalidNotice = invalidScores.length > 0
      ? `<div class="alert alert-warning border-0 mb-3"><i class="bx bx-info-circle me-1"></i>${invalidScores.length} điểm đã bị vô hiệu và không được tính vào điểm trung bình.</div>`
      : ''

    const rows = scoresArray.map(score => {
      const numericScore = parseFloat(score.total_score)
      const displayScore = Number.isFinite(numericScore) ? this.formatScore(numericScore) : '—'
      const statusBadge = score.invalidated
        ? '<span class="badge bg-secondary">Đã vô hiệu</span>'
        : '<span class="badge bg-success">Hiệu lực</span>'
      const submittedAt = this.formatTimestamp(score.submitted_at)
      const notes = score.notes ? `<div class="small text-muted mt-1"><i class="bx bx-note me-1"></i>${score.notes}</div>` : ''

      return `
        <tr>
          <td class="fw-semibold align-middle">${score.judgeName}</td>
          <td class="text-center align-middle">
            <span class="fw-bold ${score.invalidated ? 'text-muted text-decoration-line-through' : 'text-primary'}">${displayScore}</span>
          </td>
          <td class="text-center align-middle">${statusBadge}</td>
          <td class="text-center align-middle">${submittedAt}</td>
          <td class="align-middle">${notes || '<span class="text-muted">—</span>'}</td>
        </tr>
      `
    }).join('')

    const averageSection = averageScore !== null
      ? `
        <div class="card bg-primary border-primary mb-3">
          <div class="card-body text-center py-3">
            <i class="bx bx-calculator text-white fs-3 mb-2"></i>
            <div class="h4 fw-bold text-white mb-1">${this.formatScore(averageScore)}</div>
            <small class="text-white">Điểm trung bình của giám khảo đang hiệu lực</small>
          </div>
        </div>
      `
      : ''

    const html = `
      <div class="text-center mb-4">
        <h6 class="text-primary fw-bold mb-1">${participantName}</h6>
        <small class="text-muted">${summaryText}</small>
      </div>

      ${invalidNotice}

      ${averageSection}

      <div class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead class="table-light">
            <tr>
              <th>Giám khảo</th>
              <th class="text-center">Tổng điểm</th>
              <th class="text-center">Trạng thái</th>
              <th class="text-center">Thời gian</th>
              <th>Ghi chú</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </div>

      <div class="text-center pt-3 border-top mt-4">
        <small class="text-muted">
          <i class="bx bx-info-circle me-1"></i>${hasValidScores ? `Điểm trung bình từ ${validScores.length} giám khảo đang hiệu lực` : 'Không có điểm hợp lệ để tính trung bình'}
        </small>
      </div>
    `

    this.contentTarget.innerHTML = html
  }

  invalidateScore(event) {
    event.preventDefault()
    this.performScoreToggle(event.currentTarget)
  }

  restoreScore(event) {
    event.preventDefault()
    this.performScoreToggle(event.currentTarget)
  }

  performScoreToggle(button) {
    const url = button.dataset.url
    if (!url) return

    const originalHtml = button.innerHTML
    button.disabled = true
    button.innerHTML = '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Đang xử lý...'

    fetch(url, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': this.csrfToken(),
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({})
    })
      .then(response => {
        if (!response.ok) {
          throw response
        }
      })
      .then(() => {
        this.modal.hide()
        window.location.reload()
      })
      .catch(() => {
        button.disabled = false
        button.innerHTML = originalHtml
        this.showError('Không thể cập nhật điểm. Vui lòng thử lại.')
      })
  }

  csrfToken() {
    const element = document.querySelector('meta[name="csrf-token"]')
    return element ? element.content : ''
  }

  showError(message) {
    window.alert(message)
  }

  formatScore(value) {
    const numeric = parseFloat(value)
    if (!Number.isFinite(numeric)) return '—'
    return numeric.toFixed(3)
  }

  formatTimestamp(value) {
    if (!value) return 'Chưa gửi'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return 'Chưa gửi'
    return date.toLocaleString('vi-VN')
  }
}
